import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';

export type TrackingKeys = 'readMore' | 'readMoreClicked';

type TrackingRegistry = {
  has: (key: TrackingKeys) => boolean;
  register: (key: TrackingKeys) => void;
  unregister: (key: TrackingKeys) => void;
};

const TrackingContext = createContext<TrackingRegistry | null>(null);

export const TrackingProvider = ({ children }: { children: ReactNode }) => {
  const countsRef = useRef(new Map<TrackingKeys, number>());
  const [, force] = useState(0);

  const register = useCallback((key: TrackingKeys) => {
    const map = countsRef.current;
    map.set(key, (map.get(key) ?? 0) + 1);
    force((x) => x + 1);
  }, []);

  const unregister = useCallback((key: TrackingKeys) => {
    const map = countsRef.current;
    const next = Math.max(0, (map.get(key) ?? 0) - 1);
    if (next === 0) map.delete(key);
    else map.set(key, next);
    force((x) => x + 1);
  }, []);

  const has = useCallback((key: TrackingKeys) => {
    return (countsRef.current.get(key) ?? 0) > 0;
  }, []);

  const value = useMemo(() => ({ has, register, unregister }), [has, register, unregister]);

  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
};

export const useTrackingRegistry = () => {
  const ctx = useContext(TrackingContext);
  if (!ctx) throw new Error('useTrackingRegistry must be used within TrackingProvider');
  return ctx;
};
